import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'sb-react-intl'
import actions from 'redux/actions'
import { constants } from 'common/helpers'
import cx from 'classnames'

import ProductTags from 'components/product/ProductTags/ProductTags'
import Reply from 'components/review/Reply/Reply'

import UserInfo from '../UserInfo/UserInfo'
import ReviewButton from '../ReviewButton/ReviewButton'
import ReplyForm from './ReplyForm/ReplyForm'
import UserSummary from './UserSummary/UserSummary'
import Rating from './Rating/Rating'
import Date from './Date/Date'
import Title from './Title/Title'
import Description from './Description/Description'
import ProductCard from './ProductCard/ProductCard'
import Helpful from '../Helpful/Helpful'

import messages from './messages'
import s from './Review.scss'


class Review extends PureComponent {

  state = {
    isReplyFormShown: false,
  }

  setReplyFormShown = (isShown) => this.setState({ isReplyFormShown: isShown })

  handleReplyFormToggle = ({ isShown }) => {
    const { isLoggedIn } = this.props

    if (!isLoggedIn) {
      actions.models.modals.open(constants.modals.review.LoginRequired, { toWhatText: messages.toReply })
    }
    else {
      this.setReplyFormShown(isShown)
    }
  }

  handleReplySubmit = ({ message }) => {
    const { onReplySubmit, review } = this.props

    this.setReplyFormShown(false)
    onReplySubmit({ message, reviewId: review.id })
  }

  render() {
    const {
      review, starsCount, currentUserId, withMicrodata, isHelpfulBlockVisible, onEditReviewClick,
      isReplyButtonVisible, isHelpfulButtonsActive, onReplyVote, onVote,
    } = this.props

    const { isReplyFormShown } = this.state

    const {
      id, user, user: { id: authorId } = {},
      rating, date, title, text, tags, isUseful, likeCount, dislikeCount, skinType, ageCategory,
      product: { img, image, url, name } = {}, replies = [],
    } = review

    const hasAgeOrSkinType  = skinType || ageCategory
    const isHelpfulVisible  = (authorId && currentUserId && authorId !== currentUserId) || isHelpfulBlockVisible
    const isEditable        = (authorId && currentUserId && currentUserId === authorId)

    return (
      <div
        className={s.review}
        {...withMicrodata && {
          itemProp: 'review',
          itemScope: true,
          itemType: 'http://schema.org/Review',
        }}
      >
        {
          Boolean(user) && (
            <div className={s.header}>
              <UserInfo
                withMicrodata={withMicrodata}
                avatarClassName={s.reviewAvatar}
                userNameClassName={s.userName}
                {...user}
              />
              {
                Boolean(withMicrodata && user.reviewsTotal) && (
                  <UserSummary {...user} />
                )
              }
            </div>
          )
        }
        {
          Boolean(review.product) && (
            <div className={s.header}>
              <ProductCard {...{ img, image, url, name }} />
            </div>
          )
        }
        <div className={s.body}>
          <div className={s.subHeader}>
            {
              Boolean(rating) && (
                <Rating
                  {...{ withMicrodata, rating, starsCount }}
                />
              )
            }
            <Date {...{ withMicrodata, date }} />
            {
              isEditable && (
                <ReviewButton onEditReviewClick={onEditReviewClick}>
                  <div className={cx(s.editReview, { [s.left]: starsCount === 10 })}>
                    <button type="button" className={s.edit}>
                      <FormattedMessage {...messages.edit} />
                    </button>
                  </div>
                </ReviewButton>
              )
            }
          </div>
          {
            Boolean(hasAgeOrSkinType) && (
              <div className={s.additionalRow}>
                {
                  Boolean(ageCategory) && (
                    <div className={s.additionalCol}><span>Age:</span> {ageCategory}</div>
                  )
                }
                {
                  Boolean(skinType) && (
                    <div className={s.additionalCol}><span>Skin type:</span> {skinType}</div>
                  )
                }
              </div>
            )
          }
          <Title {...{ withMicrodata, title }} />
          <Description {...{ withMicrodata, text }} />
          {
            Boolean(tags && tags.length) && (
              <ProductTags className={s.tags} items={tags} />
            )
          }
          {
            isHelpfulVisible && (
              <Helpful {...{
                id,
                likeCount,
                dislikeCount,
                isUseful,
                isReplyFormShown,
                isReplyButtonVisible,
                isHelpfulButtonsActive,
                onVote,
                onReplyFormToggle: this.handleReplyFormToggle,
                dataTestIds: {
                  likeButton: constants.testIds.review.likeButton,
                  dislikeButton: constants.testIds.review.dislikeButton,
                  replyButton: constants.testIds.review.replyButton,
                },
              }} />
            )
          }
          {
            isReplyFormShown && (
              <ReplyForm
                className={s.replyForm}
                reviewId={id}
                onCancel={() => this.handleReplyFormToggle({ isShown: false })}
                onSubmit={this.handleReplySubmit}
              />
            )
          }
          {
            Boolean(replies.length) && (
              <div className={s.replies}>
                {
                  replies.map((reply) => (
                    <Reply
                      key={reply.id}
                      reply={reply}
                      reviewId={id}
                      currentUserId={currentUserId}
                      onVote={onReplyVote}
                    />
                  ))
                }
              </div>
            )
          }
        </div>
      </div>
    )
  }
}

Review.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.number.isRequired,
    user: PropTypes.object,
    rating: PropTypes.number.isRequired,
    date: PropTypes.string.isRequired,
    title: PropTypes.string,
    text: PropTypes.string.isRequired,
    tags: PropTypes.array.isRequired,
    isUseful: PropTypes.bool,
    likeCount: PropTypes.number,
    dislikeCount: PropTypes.number,
  }).isRequired,
  currentUserId: PropTypes.number,
  onVote: PropTypes.func,
  onReplyFormToggle: PropTypes.func,
  withMicrodata: PropTypes.bool,
  onReplySubmit: PropTypes.func,
  replies: PropTypes.node,
  isHelpfulBlockVisible: PropTypes.bool,
  isReplyButtonVisible: PropTypes.bool,
  isHelpfulButtonsActive: PropTypes.bool,
}

Review.defaultProps = {
  isReplyButtonVisible: true,
  isHelpfulButtonsActive: true,
}


export default Review
